'use client'

/**
 * RevenueCat SDK client for Capacitor. All functions no-op when not in Capacitor.
 * Uses dynamic import so the SDK is never loaded on web or in tests.
 */

export type PaywallResult = 'PURCHASED' | 'RESTORED' | 'CANCELLED' | 'ERROR' | 'NOT_PRESENTED'

export interface PresentPaywallResult {
  result: PaywallResult
  error?: string
}

function isCapacitor(): boolean {
  if (typeof window === 'undefined') return false
  // window.Capacitor existe también en web (shim de @capacitor/core); solo es app
  // native cuando isNativePlatform() lo confirma. Evita cargar el SDK en web.
  const cap = (window as unknown as { Capacitor?: { isNativePlatform: () => boolean } }).Capacitor
  return cap?.isNativePlatform() === true
}

/**
 * Configure RevenueCat Purchases. Call once when app is ready and user may be logged in.
 * Uses NEXT_PUBLIC_REVENUECAT_API_KEY_IOS / ANDROID from env.
 */
export async function configureRevenueCat(appUserId?: string): Promise<void> {
  if (!isCapacitor()) return
  try {
    const { Purchases, LOG_LEVEL } = await import('@revenuecat/purchases-capacitor')
    const { Capacitor } = await import('@capacitor/core')
    const iosKey = process.env.NEXT_PUBLIC_REVENUECAT_API_KEY_IOS || ''
    const androidKey = process.env.NEXT_PUBLIC_REVENUECAT_API_KEY_ANDROID || ''
    const platform = Capacitor.getPlatform()
    const apiKey = platform === 'ios' ? iosKey : platform === 'android' ? androidKey : iosKey || androidKey
    if (!apiKey) {
      console.warn('[RevenueCat] No API key for platform:', platform)
      return
    }
    if (process.env.NODE_ENV === 'development') {
      await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG })
    }
    await Purchases.configure({
      apiKey,
      appUserID: appUserId ?? undefined,
    })
  } catch (err) {
    console.error('[RevenueCat] Configure error:', err)
  }
}

/**
 * Log in RevenueCat with app user id (e.g. after auth). Call after configure when user logs in.
 */
export async function logInRevenueCat(appUserId: string): Promise<void> {
  if (!isCapacitor()) return
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor')
    await Purchases.logIn({ appUserID: appUserId })
  } catch (err) {
    console.error('[RevenueCat] LogIn error:', err)
  }
}

/**
 * Present RevenueCat Paywall. Returns result; on PURCHASED/RESTORED caller should refresh account (e.g. refreshAccount()).
 */
export async function presentRevenueCatPaywall(): Promise<PresentPaywallResult> {
  if (!isCapacitor()) {
    return { result: 'NOT_PRESENTED' }
  }
  try {
    const { RevenueCatUI } = await import('@revenuecat/purchases-capacitor-ui')
    const { PAYWALL_RESULT } = await import('@revenuecat/purchases-capacitor')
    const rawResult = await RevenueCatUI.presentPaywall()
    const result = rawResult?.result ?? rawResult
    // Native may return object { result: 'ERROR', error?: string } or a string
    const resultStr =
      typeof result === 'object' && result !== null && 'result' in result
        ? String((result as { result: string }).result)
        : String(result ?? '')
    const errMsg =
      typeof rawResult === 'object' &&
      rawResult !== null &&
      'error' in rawResult &&
      typeof (rawResult as { error?: string }).error === 'string'
        ? (rawResult as { error: string }).error
        : undefined
    if (
      resultStr === PAYWALL_RESULT.PURCHASED ||
      resultStr === PAYWALL_RESULT.RESTORED
    ) {
      return { result: resultStr as PaywallResult }
    }
    if (
      resultStr === PAYWALL_RESULT.CANCELLED ||
      resultStr === PAYWALL_RESULT.NOT_PRESENTED
    ) {
      return { result: resultStr as PaywallResult }
    }
    // ERROR or any other (e.g. purchase failure in Test Store)
    return { result: 'ERROR', error: errMsg || resultStr || 'Unknown result' }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err)
    const code = err && typeof err === 'object' && 'code' in err ? (err as { code: string }).code : ''
    // Plugin not implemented = running in browser or native app built without RevenueCat pods
    if (code === 'UNIMPLEMENTED') {
      console.warn('[RevenueCat] Purchases plugin not available (run app from Xcode after pod install)')
      return { result: 'NOT_PRESENTED' }
    }
    console.error('[RevenueCat] PresentPaywall error:', err)
    return { result: 'ERROR', error: message }
  }
}

/**
 * Present RevenueCat Customer Center (manage subscription / restore / support).
 */
export async function presentRevenueCatCustomerCenter(): Promise<void> {
  if (!isCapacitor()) return
  try {
    const { RevenueCatUI } = await import('@revenuecat/purchases-capacitor-ui')
    await RevenueCatUI.presentCustomerCenter()
  } catch (err) {
    console.error('[RevenueCat] PresentCustomerCenter error:', err)
  }
}
